/* eslint-disable react/prop-types */
import React, { Suspense, useEffect, useRef, startTransition } from 'react';
import { useSelector } from 'react-redux';
import { HashRouter, Route, Routes, Navigate } from 'react-router-dom';
import './scss/datagrid.scss';
import './scss/style.scss';
import '../src/CSS/Form.css';
import '../src/CSS/Style.css';
import ConfirmationModal from './components/Modals/ConfirmationModal';
// ⚡ Direct imports - no lazy loading for public pages
import ForgotPassword from './pages/Auth/ForgotPassword';
import AddDA from './pages/DA/AddDA';
import DADspRegister from './pages/DA/DADspRegister';
import Faqs from './pages/FAQ/Faqs';
import PrivacyPolicy from './pages/PrivacyPolicy/PrivacyPolicy';
import useApi from './hooks/useApi';
import { useShowToast } from './hooks/useShowToast';
import { useLogout } from './hooks/api/useLogout';
import { addGlobalUtils } from './helpers/utilHelper';

const Loading = React.lazy(() => import('../src/components/UI/Loading'));

// Only lazy load pages that need authentication
const Index = React.lazy(() => import('./pages/Auth/SignIn'));
const Page404 = React.lazy(() => import('./pages/Error/Page404'));
const Page500 = React.lazy(() => import('./pages/Error/Page500'));
const Page401 = React.lazy(() => import('./pages/Error/Page401'));

// Auth Route Wrapper
const AuthRoute = ({ isAuthenticated, children, user }) => {
  return children;
};

const App = () => {
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const user = useSelector((state) => state.user);
  const confirMdl = useSelector((state) => state.confirMdl);
  const showToast = useShowToast();
  const { logout } = useLogout();
  const isLoggingOut = useRef(false);
  const { loading: utilLoading, postData: fetchUtils } = useApi('/Common/lovs');

  const getUtils = async () => {
    const res = await fetchUtils();
    if (res?.status === true) {
      addGlobalUtils(res?.data);
    } else {
      showToast(res?.message, 'error');
    }
  };

  useEffect(() => {
    getUtils();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      isLoggingOut.current = false;
      return;
    }

    const AUTH_TOKEN_KEY = 'states';

    const checkLocalStorage = () => {
      if (isLoggingOut.current) return;

      const token = localStorage.getItem(AUTH_TOKEN_KEY);

      if (isAuthenticated && !token) {
        isLoggingOut.current = true;
        logout();
      }
    };

    checkLocalStorage();

    const interval = setInterval(checkLocalStorage, 1000);

    const handleStorageChange = (e) => {
      if (isLoggingOut.current) return;

      if (e.key === AUTH_TOKEN_KEY && e.newValue === null && isAuthenticated) {
        isLoggingOut.current = true;
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      clearInterval(interval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, logout]);

  return (
    <>
      <HashRouter>
        <Routes>
          {/* Auth routes with Suspense - wrap EACH route individually */}
          <Route
            exact
            path="/"
            name="Index Page"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <AuthRoute isAuthenticated={isAuthenticated} user={user}>
                  <Index />
                </AuthRoute>
              </Suspense>
            }
          />

          <Route
            exact
            path="/forgot-password"
            name="Forgot Password"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <AuthRoute isAuthenticated={isAuthenticated} user={user}>
                  <ForgotPassword />
                </AuthRoute>
              </Suspense>
            }
          />

          {/* ⚡ Public routes - NO Suspense, instant loading */}
          <Route exact path="/faqs" name="Register Page" element={<Faqs />} />
          <Route exact path="/AddDA/:dsp?" name="AddDA" element={<AddDA />} />
          <Route exact path="/PrivacyPolicy" name="PrivacyPolicy" element={<PrivacyPolicy />} />

          {!isAuthenticated && (
            <Route exact path="/dspRegister" name="DspRegister" element={<DADspRegister />} />
          )}

          {/* Error routes with Suspense */}
          <Route
            exact
            path="/404"
            name="Page 404"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <Page404 />
              </Suspense>
            }
          />
          <Route
            exact
            path="/500"
            name="Page 500"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <Page500 />
              </Suspense>
            }
          />

          <Route
            exact
            path="*"
            element={
              <Suspense fallback={<div>Loading...</div>}>
                <Page401 isAuthenticated={isAuthenticated} />
              </Suspense>
            }
          />
        </Routes>
      </HashRouter>
      {!isAuthenticated && (
        <ConfirmationModal
          header={confirMdl.header}
          body={confirMdl.body}
          isOpen={confirMdl.isOpen && !isAuthenticated}
          onYes={confirMdl.onYes}
          onNo={confirMdl.onNo}
          loading={confirMdl.loading}
        />
      )}
    </>
  );
};

export default App;
